import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, RefreshCw } from 'lucide-react';
import * as api from '../services/api';

const SERIF_FONT = "'Playfair Display', 'Georgia', serif";
const SANS_FONT = "'Inter', -apple-system, sans-serif";

const StaticPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchPage = async () => {
      try {
        const data = await api.getStaticPage(slug);
        if (!data.is_published) {
          setError('Esta página no está disponible');
        } else {
          setPage(data);
        }
      } catch (err) {
        setError('Página no encontrada');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPage();
  }, [slug]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <p className="text-gray-500 mb-4">{error}</p>
        <Link to="/" className="text-[#1e3a5f] hover:underline">
          Volver al inicio
        </Link>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
            style={{ fontFamily: SANS_FONT }}
          >
            <ArrowLeft className="w-4 h-4" />
            Inicio
          </Link>
        </div>
      </div>
      
      {/* Content */}
      <article className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <header className="mb-12 text-center">
          <h1 
            className="text-4xl lg:text-5xl font-bold mb-4"
            style={{ fontFamily: SERIF_FONT }}
          >
            {page.title}
          </h1>
          {page.meta_description && (
            <p 
              className="text-lg text-gray-500 max-w-2xl mx-auto"
              style={{ fontFamily: SANS_FONT }}
            >
              {page.meta_description}
            </p>
          )}
        </header>
        
        {/* Main Content */}
        <div 
          className="prose prose-lg max-w-none
            prose-headings:font-bold prose-headings:text-gray-900
            prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
            prose-p:text-gray-700 prose-p:leading-relaxed
            prose-a:text-[#1e3a5f] prose-a:no-underline hover:prose-a:underline
            prose-ul:my-4 prose-li:my-1
            prose-strong:text-gray-900"
          style={{ fontFamily: SANS_FONT }}
          dangerouslySetInnerHTML={{ __html: page.content }}
        />
        
        {/* Footer */}
        <footer className="mt-16 pt-8 border-t">
          <p className="text-sm text-gray-400 text-center" style={{ fontFamily: SANS_FONT }}>
            Última actualización: {new Date(page.updated_at).toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </footer>
      </article>
    </div>
  );
};

export default StaticPage;
